import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  getStoredToken,
  loginClient,
  updateAuthorizationHeader,
} from "../../api/axiosClient";
import { Button, Result } from "antd";
import { Loading } from "../../components";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getMe } from "../../redux/apiActions/authActions";
import { getAuthState } from "../../redux/store/getState";
import handleApiError from "../../utils/handleError";
import {
  clearPendingRedirect,
  rememberPendingRedirect,
} from "../../utils/pendingRedirect";

/**
 * SSO landing page for the Xperience deep-link: /auth/<jwt>?redirect=<app path>.
 *
 * The handoff has to be deterministic — a student pressing "Start Test" in
 * Xperience must end up inside their proctored exam, never on the portal's home
 * page wondering what happened. Two rules keep it that way:
 *
 *  1. Navigate only once *this* exchange has produced a session. Keying the
 *     redirect off `userType` alone meant a pre-existing (stale) session could
 *     trigger the jump before the new token was installed, and the exam page then
 *     loaded against a session that was about to be torn down.
 *  2. Park the redirect target before doing anything that can navigate, so a
 *     bounce (cleared session, reload, /login) can still finish the handoff.
 */
export default function CallBackLogin() {
  const [error, setError] = useState(false);
  // Set only when the session minted by *this* callback is loaded in redux.
  const [ready, setReady] = useState(false);
  const params = useParams();
  const dispatch = useAppDispatch();
  const token = params.token;

  const { homeRoute } = useAppSelector(getAuthState);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Captured once, on the first render: nothing that happens later (a re-render, a
  // route change, a reload) can lose the deep-link target.
  const redirectRef = useRef(searchParams.get("redirect") || "");

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;

    async function login() {
      if (redirectRef.current) rememberPendingRedirect(redirectRef.current);
      try {
        const res: any = await loginClient.get(`/auth/${token}`);
        if (res.data.authorization_token) {
          updateAuthorizationHeader(res.data.authorization_token);
        }
        await dispatch(getMe()).unwrap();
        if (!cancelled) setReady(true);
      } catch (error: any) {
        // The deep-link token is single-use, so a reload or a Back-navigation onto
        // this URL replays a spent token. If we already hold a portal session,
        // carry on with it instead of showing a dead 403 page.
        if (getStoredToken()) {
          try {
            await dispatch(getMe()).unwrap();
            if (!cancelled) setReady(true);
            return;
          } catch {
            // Session is genuinely gone — fall through to the error screen.
          }
        }
        if (cancelled) return;
        handleApiError(error);
        setError(true);
      }
    }

    login();
    return () => {
      cancelled = true;
    };
  }, [token, dispatch]);

  useEffect(() => {
    if (!ready) return;
    const target = redirectRef.current || homeRoute;
    clearPendingRedirect();
    // `replace`, because /auth/<jwt> is spent — leaving it in history means Back
    // lands the student on a "token already used" error.
    navigate(target, { replace: true });
  }, [ready, homeRoute, navigate]);

  if (error)
    return (
      <div style={{ background: "#1c1c1c", color: "#fff", height: "100%" }}>
        <Result
          status="403"
          title="403"
          subTitle={
            <h4 style={{ color: "#fff" }}>
              Sorry, you are not authorized to access this page.
            </h4>
          }
          extra={
            <Button
              type="primary"
              onClick={() => {
                window.location.href = "/login";
              }}
            >
              Back to login
            </Button>
          }
        />
      </div>
    );
  return <Loading />;
}
